import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { format } from 'date-fns';

interface Stats {
  active_failures: {
    count: number;
    total_amount: number;
  };
  active_suspensions: number;
}

interface PaymentFailure {
  id: number;
  custid: number;
  customer_name: string;
  CompanyName: string;
  email: string;
  invoice_number: string;
  original_amount: string;
  failure_date: string;
  failure_reason: string;
  days_overdue: number;
  days_until_suspension: number;
  suspended_at: string | null;
  notifications_sent: string[];
}

interface Suspension {
  id: number;
  custid: number;
  customer_name: string;
  CompanyName: string;
  email: string;
  suspension_date: string;
  original_amount: string;
  services_suspended: any;
}

const DunningNew: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Overview state
  const [stats, setStats] = useState<Stats | null>(null);
  
  // Failures state
  const [failures, setFailures] = useState<PaymentFailure[]>([]);
  const [failuresPage, setFailuresPage] = useState(0);
  const [failuresRowsPerPage, setFailuresRowsPerPage] = useState(25);
  const [failuresTotal, setFailuresTotal] = useState(0);
  
  // Suspensions state
  const [suspensions, setSuspensions] = useState<Suspension[]>([]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return;
      }

      let url = '';
      switch (activeTab) {
        case 0: // Overview
          url = 'http://localhost:8080/api/v1/endpoints/dunning-simple?action=overview';
          break;
        case 1: // Payment Failures
          url = `http://localhost:8080/api/v1/endpoints/dunning-simple?action=failures&page=${failuresPage + 1}&limit=${failuresRowsPerPage}`;
          break;
        case 2: // Suspensions
          url = 'http://localhost:8080/api/v1/endpoints/dunning-simple?action=suspensions';
          break;
      }

      console.log('[DunningNew] Fetching:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[DunningNew] Response:', data);

      if (data.success) {
        switch (activeTab) {
          case 0:
            setStats(data.data);
            break;
          case 1:
            setFailures(data.data.failures || []);
            setFailuresTotal(data.data.pagination?.total || 0);
            break;
          case 2:
            setSuspensions(data.data.suspensions || []);
            break;
        }
      } else {
        throw new Error(data.message || 'Failed to load data');
      }
    } catch (err: any) {
      console.error('[DunningNew] Error:', err);
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, failuresPage, failuresRowsPerPage]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  const getSeverityColor = (days: number): 'error' | 'warning' | 'success' => {
    if (days <= 0) return 'error';
    if (days <= 7) return 'warning';
    return 'success';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dunning Management (New)</Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Payment Failures" />
          <Tab label="Suspensions" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 0 && stats && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Active Payment Failures
                    </Typography>
                    <Typography variant="h4">
                      {stats.active_failures.count}
                    </Typography>
                    <Typography variant="body2" color="error">
                      Total: ${stats.active_failures.total_amount.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Active Suspensions
                    </Typography>
                    <Typography variant="h4">
                      {stats.active_suspensions}
                    </Typography>
                    <Typography variant="body2">
                      Services currently suspended
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Payment Failures Tab */}
          {activeTab === 1 && (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Failed Date</TableCell>
                      <TableCell>Days Overdue</TableCell>
                      <TableCell>Suspension In</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failures.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography py={4}>No payment failures found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      failures.map((failure) => (
                        <TableRow key={failure.id}>
                          <TableCell>
                            <Typography variant="body2">{failure.customer_name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {failure.email}
                            </Typography>
                          </TableCell>
                          <TableCell>{failure.invoice_number}</TableCell>
                          <TableCell>${parseFloat(failure.original_amount).toFixed(2)}</TableCell>
                          <TableCell>{format(new Date(failure.failure_date), 'MMM dd, yyyy')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${failure.days_overdue} days`}
                              color={failure.days_overdue > 30 ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {failure.suspended_at ? (
                              <Chip label="Suspended" color="error" size="small" />
                            ) : (
                              <Chip 
                                label={`${failure.days_until_suspension} days`}
                                color={getSeverityColor(failure.days_until_suspension)}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {failure.notifications_sent.length > 0 ? (
                              <Typography variant="caption">
                                {failure.notifications_sent.length} notices sent
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                No notices sent
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {failuresTotal > 0 && (
                <TablePagination
                  component="div"
                  count={failuresTotal}
                  page={failuresPage}
                  onPageChange={(e, page) => setFailuresPage(page)}
                  rowsPerPage={failuresRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setFailuresRowsPerPage(parseInt(e.target.value));
                    setFailuresPage(0);
                  }}
                />
              )}
            </Paper>
          )}

          {/* Suspensions Tab */}
          {activeTab === 2 && (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Suspension Date</TableCell>
                      <TableCell>Amount Due</TableCell>
                      <TableCell>Services Affected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suspensions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography py={4}>No active suspensions</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      suspensions.map((suspension) => (
                        <TableRow key={suspension.id}>
                          <TableCell>
                            <Typography variant="body2">{suspension.customer_name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {suspension.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {format(new Date(suspension.suspension_date), 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell>
                            ${parseFloat(suspension.original_amount || '0').toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {typeof suspension.services_suspended === 'object' 
                                ? Object.keys(suspension.services_suspended).length + ' services'
                                : 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default DunningNew;